import {
  HoverCard,
  HoverCardArrow,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useNavigate } from "react-router"
import { McPill } from "./McPill"

export interface RelBadgeItem {
  id: string
  primary: string
  secondary?: string
}

interface RelBadgeProps {
  /** Label shown in the badge, e.g. "articles" / "substances" */
  label: string
  /** Singular label used when `count` is exactly 1, e.g. "article". Falls back to `label`. */
  labelOne?: string
  count: number
  /** "danger" when a restricted substance is present in this relation, "muted" otherwise */
  tone: "danger" | "muted"
  /** Top-4 preview items from the list API's rel_* field */
  items: RelBadgeItem[]
  /** Route to the related entity's own detail view, e.g. (id) => `/material-compliance/master-data/substances?id=${id}&source=nav` */
  itemHref?: (id: string) => string
  /** Present only when a detail-panel section exists to show the full relation (e.g. ArticleSubstancesSection) */
  onShowAll?: () => void
}

export function RelBadge({
  label,
  labelOne,
  count,
  tone,
  items,
  itemHref,
  onShowAll,
}: RelBadgeProps) {
  const navigate = useNavigate()
  const { translate } = useTranslation()

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span className="cursor-default">
          <McPill tone={tone}>
            {count} {count === 1 && labelOne ? labelOne : label}
          </McPill>
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        side="bottom"
        align="start"
        className="w-72 border-0 bg-grey2 p-3 shadow-none filter-[drop-shadow(0_0_1px_#515359)_drop-shadow(0_8px_16px_rgb(0_0_0/0.35))]"
        onClick={(e) => e.stopPropagation()}
      >
        {items.length === 0 ? (
          <p className="text-[11.5px] text-grey5">
            {translate("MC_MD_REL_NO_RELATED", { label })}
          </p>
        ) : (
          <ul className="space-y-1.5">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 rounded px-1 py-0.5 text-left hover:bg-white/4"
                  onClick={() =>
                    itemHref ? navigate(itemHref(item.id)) : undefined
                  }
                  disabled={!itemHref}
                >
                  <span className="min-w-0 flex-1 truncate text-[11.5px] text-white">
                    {item.primary}
                  </span>
                  {item.secondary && (
                    <span className="max-w-[45%] shrink-0 truncate text-[11px] text-grey5">
                      {item.secondary}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        {onShowAll && count > items.length && (
          <button
            type="button"
            onClick={onShowAll}
            className="mt-2 border-t border-white/5 pt-2 text-[11.5px] font-semibold text-grey5 hover:text-white"
          >
            {translate("MC_MD_REL_SHOW_ALL", { count })}
          </button>
        )}
        <HoverCardArrow className="fill-grey2" />
      </HoverCardContent>
    </HoverCard>
  )
}
